import S from 's-js';
import * as _ from 'underscore';
import UOM from 'lib/UOM';
import { Measurement, NullableMeasurementLike } from 'data/models/Measurement';
import { ConvertibleMeasurement, NullableConvertibleMeasurement } from 'lib/ConvertibleMeasurement';
import { ConvertedMeasurement, BindingMode } from 'lib/ConvertedMeasurement';

interface FeetAndInches {
    feet : number | null
    inches : number | null
}

export type UnitsStyle = 'ticks' | 'pretty-ticks' | 'abbrev' | 'abbrev-period' | 'full';

export interface ToStringOptions {
    /** Controls how feet and inches units are formatted. */
    unitsStyle? : UnitsStyle;
    /** Determines whether inches should be left padded with a zero to ensure two digits. */
    padInchesLeft? : boolean;
}

const FEET_UNITS : { [ style in UnitsStyle ] : string } = {
    ticks               : '\'',
    "pretty-ticks"      : '\u2019',
    abbrev              : 'ft',
    "abbrev-period"     : 'ft.',
    full                : 'feet'
};

const INCHES_UNITS : { [ style in UnitsStyle ] : string } = {
    ticks               : '"',
    "pretty-ticks"      : '\u201D',
    abbrev              : 'in',
    "abbrev-period"     : 'in.',
    full                : 'inches'
};

const SPACE_BEFORE_UNITS : { [ style in UnitsStyle ] : string } = {
    ticks               : '', // No space
    "pretty-ticks"      : '', // No space
    abbrev              : ' ', // Yes space
    "abbrev-period"     : ' ', // Yes space
    full                : ' ', // Yes space
};

const SPACE_AFTER_FT_UNITS : { [ style in UnitsStyle ] : string } = {
    ticks               : '', // No space
    "pretty-ticks"      : ' ', // Yes space
    abbrev              : ' ', // Yes space
    "abbrev-period"     : ' ', // Yes space
    full                : ' ', // Yes space
};

/// <summary>
/// Like a ConvertedMeasurement, but w/ Feet and Inches observables instead of standard Num and UOM.
///
/// Like ConvertedMeasurement, for reads backingMeasurement can be a plain Measurement instance, a ConvertibleMeasurement, or a Measurement-like object w/ Num and UOM observables.
/// For writes, backingMeasurement must be a ConvertibleMeasurement.
/// </summary>
export class MeasurementInFeetAndInches {
    Feet : (val? : number | null) => number | null
    Inches : (val? : number | null) => number | null

    private _converted : ConvertedMeasurement;

    constructor(backingMeasurement : NullableMeasurementLike, decimalPlaces : number) {
        decimalPlaces = decimalPlaces || 0;
        var converted = this._converted = new ConvertedMeasurement(backingMeasurement, UOM.Foot, 20);

        var lastWrite = S.data<FeetAndInches | null>(null);

        var feetAndInchesObj = function (obj? : FeetAndInches) {
            if (obj === undefined) {
                if (converted.BindingMode() === BindingMode.WRITE) return lastWrite();
                return _split(converted.Num(), decimalPlaces);
            } else {
                lastWrite(obj);

                var curr = _split(S.sample(converted.Num), decimalPlaces);

                // TH: 4/15/2016 -- Bugfix. Don't write to the backingMeasurement if the numeric value of the feet and inches fields are staying the same.
                // That prevents accidental changes caused by tabbing through a field or using arrow keys w/ in a field.
                // This comes up when there are multiple displays for the same backingMeasurement w/ a precision mismatch between the displays
                if (curr.feet !== obj.feet || curr.inches !== obj.inches) {
                    converted.Num(_join(obj.feet, obj.inches));
                }
                return obj;
            }
        };

        this.Feet   = computedFeetOrInches('feet');
        this.Inches = computedFeetOrInches('inches');

        function computedFeetOrInches(key : 'feet' | 'inches') {
            return function (val? : number | null) {
                if (val === undefined) {
                    var obj = feetAndInchesObj();
                    return !obj ? null : key === 'feet' ? obj.feet : obj.inches;
                } else {
                    if (!(backingMeasurement instanceof ConvertibleMeasurement
                        || backingMeasurement instanceof NullableConvertibleMeasurement)) {
                        throw new Error('Can\'t write to a MeasurementInFeetAndInches unless its backingMeasurement is a ConvertibleMeasurement or NullableConvertibleMeasurement');
                    }
                    var obj = S.sample(feetAndInchesObj);
                    obj = obj || { feet : null, inches : null };
                    key === 'feet' ? obj.feet = val : obj.inches = val;
                    feetAndInchesObj(obj);
                    return val;
                }
            };
        }
    }

    toString({ unitsStyle = 'ticks', padInchesLeft = true } : ToStringOptions = {}) {
        let feet = this.Feet(), inches = this.Inches(), strInches : string;
        if (feet === null && inches === null) return '--';

        feet = feet || 0; inches = inches || 0;

        if (inches < 0 || inches - Math.floor(inches) > 0) {
            // Don't even bother trying to pretty-format negative or fractional inches
            // These can only happen as a result of direct user input to the inches field
            strInches = inches.toString();
        } else if (padInchesLeft) {
            // Pad inches to two digits
            strInches = ('00' + inches).substr(-2);
        } else {
            strInches = inches.toString();
        }

        return feet.toLocaleString() +
            SPACE_BEFORE_UNITS[unitsStyle] +
            FEET_UNITS[unitsStyle] +
            SPACE_AFTER_FT_UNITS[unitsStyle] +
            strInches +
            SPACE_BEFORE_UNITS[unitsStyle] +
            INCHES_UNITS[unitsStyle];
    }

    RelinquishWriter() {
        this._converted.RelinquishWriter();
    }
}

// Split number of feet into an object w/ (whole) feet and (possibly fractional) inches.
// NOTE: Handling negative values:
//   The idea is to support UX conventions where a negative feet value makes the whole value negative. E.g.:
//      -22 ft. 6 in. would mean -(22'6")
//   So -22.5 would split as { feet: -22, inches: 6 } b/c that's what needs to go in the input boxes.
//   This is admittedly counterintuitive. Hopefully negatives will hardly ever come up.
function _split(feet : number | null, places : number) : FeetAndInches {
    if (feet === null) return { feet: null, inches: null };
    const
        totalInches = feet * UOM.Conversion[UOM.Inch][UOM.Foot]!,
        // Apply rounding to total inches before splitting into feet and inches. This handles
        // cases like _split(10.99, 0) which should be 11'0" and NOT 10'12"
        roundedTotalInches = parseFloat(totalInches.toFixed(places)),
        roundedFeet = roundedTotalInches * UOM.Conversion[UOM.Foot][UOM.Inch]!,
        feetWhole = roundedFeet >= 0 ? Math.floor(roundedFeet) : Math.ceil(roundedFeet),
        delta = roundedFeet >= 0 ? 1 : -1,
        inches = delta * (roundedFeet - feetWhole) * UOM.Conversion[UOM.Inch][UOM.Foot]!;

    return {
        feet: feetWhole,
        // Re-round inches before returning. This corrects floating point errors.
        inches: parseFloat(inches.toFixed(places))
    };
}

// Join a number of feet and inches into a single number of feet.
// NOTE: Handling of negative values:
//   The idea is to support UX conventions where a negative feet value makes the whole value negative. E.g.:
//      -22 ft. 6 in. would mean -(22'6")
//   So _join(-22, 6) would come out as -22.5.
//   Essentially, the sign of the second argument flips from what you'd expect if the first argument is negative.
//   Oddly, this makes _join(-22, -6) come to -21.5. Again, hopefully negatives will hardly ever come up.
function _join(feet : number | null, inches : number | null) : number | null {
    if (feet === null && inches === null) return null;
    feet = feet || 0;
    inches = inches || 0;
    const delta = feet >= 0 ? 1 : -1;
    return feet + delta * inches * UOM.Conversion[UOM.Foot][UOM.Inch]!;
}



// WEBPACK FOOTER //
// ./lib/MeasurementInFeetAndInches.ts